import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProductRankingPage.css';
import PriceHistoryChart from '../components/PriceHistoryChart';

// Mapping für alte Phasenbezeichnungen auf neue
const normalizePhase = (phase) => {
  const mapping = {
    'Wachstumsphase (Veg)': 'Wachstum',
    'Blütephase (Flower)': 'Blüte'
  };
  return mapping[phase] || phase;
};

function ProductRankingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const addCacheBuster = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('t', Math.floor(Date.now() / 60000));
      return urlObj.toString();
    } catch {
      return url;
    }
  };

  const isFertilizerProduct = (prod) => {
    if (!prod) return false;
    const typeName = prod.productTypeId?.name || '';
    return typeName.toLowerCase().includes('dünger');
  };

  const isLightingProduct = (prod) => {
    if (!prod) return false;
    const typeName = prod.productTypeId?.name || '';
    return typeName.toLowerCase().includes('beleuchtung');
  };

  const fetchProductWithPrices = async () => {
    try {
      setLoading(true);

      // Lade Produkt und Preise aus der Datenbank
      const res = await fetch(`/api/products/${id}?lean=true`);
      const data = await res.json();
      
      setProduct(data.product);
      
      // Filtere nur Preise mit echten Werten und sortiere aufsteigend
      const validPrices = (data.prices || [])
        .filter(p => p.price !== null && p.price !== undefined && p.price > 0)
        .map(p => ({
          ...p,
          productImage: addCacheBuster(p.productImage)
        }))
        .sort((a, b) => a.price - b.price);
      setPrices(validPrices);
      setError('');
    } catch (err) {
      console.error('Fehler beim Laden des Produkts:', err);
      setError('Fehler beim Laden des Produkts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductWithPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="product-ranking-page">
        <div className="suche-loading">⏳ Lädt Produkt...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-ranking-page">
        <div className="suche-error">
          {error || 'Produkt nicht gefunden'}
          <button onClick={() => navigate('/')} className="btn-back">
            Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  // Preise sind bereits gefiltert
  const pricesWithValues = prices;
  const bestPrice = pricesWithValues[0]?.price || 0;
  const worstPrice = pricesWithValues[pricesWithValues.length - 1]?.price || 0;

  // Live-Berechnungen für Dünger (basierend auf günstigstem Preis)
  const bottleSizeLiters = product.fertilizerSpecs?.bottleSizeLiters;
  const dosagePerLiter = product.fertilizerSpecs?.dosagePerLiterNum;
  const dosageVeg = product.fertilizerSpecs?.dosageVegMlPerLiter;
  const dosageBloom = product.fertilizerSpecs?.dosageBloomMlPerLiter;
  const vegSchedule = product.fertilizerSpecs?.vegSchedule;
  const bloomSchedule = product.fertilizerSpecs?.bloomSchedule;
  const calcPricePerLiter = (bestPrice > 0 && bottleSizeLiters > 0)
    ? bestPrice / bottleSizeLiters
    : null;
  const calcSolutionPerEuro = (bestPrice > 0 && bottleSizeLiters > 0 && dosagePerLiter > 0)
    ? (bottleSizeLiters * 1000) / (dosagePerLiter * bestPrice)
    : null;
  const calcSolutionPerEuroVeg = (bestPrice > 0 && bottleSizeLiters > 0 && dosageVeg > 0)
    ? (bottleSizeLiters * 1000) / (dosageVeg * bestPrice)
    : null;
  const calcSolutionPerEuroBloom = (bestPrice > 0 && bottleSizeLiters > 0 && dosageBloom > 0)
    ? (bottleSizeLiters * 1000) / (dosageBloom * bestPrice)
    : null;

  // Berechnung: Wie viel Liter fertige Nährlösung aus dem Gebinde
  const calcTotalSolutionVeg = (bottleSizeLiters > 0 && dosageVeg > 0)
    ? (bottleSizeLiters * 1000) / dosageVeg
    : null;
  const calcTotalSolutionBloom = (bottleSizeLiters > 0 && dosageBloom > 0)
    ? (bottleSizeLiters * 1000) / dosageBloom
    : null;
  const calcTotalSolution = (bottleSizeLiters > 0 && dosagePerLiter > 0)
    ? (bottleSizeLiters * 1000) / dosagePerLiter
    : null;

  // Helper: parse schedule string into tooltip text
  const formatScheduleTooltip = (scheduleStr, phase) => {
    if (!scheduleStr) return '';
    const vals = scheduleStr.split(',').map(v => v.trim());
    return vals.map((v, i) => `WK${i + 1}: ${v} ml/L`).join('\n');
  };

  // Zykluskosten: 10L Nährlösung pro Woche, Wochen aus Schedule
  const cycleCostData = (() => {
    if (!calcPricePerLiter || calcPricePerLiter <= 0) return null;
    const LITERS_PER_WEEK = 10;
    let totalMl = 0;
    let vegWeeks = 0, bloomWeeks = 0;
    let vegMl = 0, bloomMl = 0;
    if (vegSchedule) {
      const vals = vegSchedule.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
      vegWeeks = vals.length;
      vals.forEach(ml => { vegMl += ml * LITERS_PER_WEEK; });
      totalMl += vegMl;
    }
    if (bloomSchedule) {
      const vals = bloomSchedule.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
      bloomWeeks = vals.length;
      vals.forEach(ml => { bloomMl += ml * LITERS_PER_WEEK; });
      totalMl += bloomMl;
    }
    if (totalMl <= 0) return null;
    const totalLitersConcentrate = totalMl / 1000;
    const cost = totalLitersConcentrate * calcPricePerLiter;
    // Tooltip-Text
    const lines = [`💰 Zykluskosten-Berechnung:`, `10L Nährlösung/Woche`];
    if (vegWeeks > 0) lines.push(`🌱 Veg: ${vegWeeks} Wo × Ø${dosageVeg} ml/L = ${vegMl.toFixed(0)} ml`);
    if (bloomWeeks > 0) lines.push(`🌸 Bloom: ${bloomWeeks} Wo × Ø${dosageBloom} ml/L = ${bloomMl.toFixed(0)} ml`);
    lines.push(`Gesamt: ${totalMl.toFixed(0)} ml = ${totalLitersConcentrate.toFixed(3)}L`);
    lines.push(`${totalLitersConcentrate.toFixed(3)}L × €${calcPricePerLiter.toFixed(2)}/L = €${cost.toFixed(2)}`);
    return { cost, tooltip: lines.join('\n') };
  })();
  const calcCycleCost = cycleCostData?.cost ?? null;

  return (
    <div className="product-ranking-page">
      {/* Produktdetails oben */}
      <div className="product-header-section">
        <button onClick={() => navigate('/')} className="btn-back-small">
          ← Zurück
        </button>
        
        <div className="product-header-content">
          {/* Produktbild */}
          <div className="product-header-image">
            {prices[0]?.productImage || product.image ? (
              <img src={prices[0]?.productImage || addCacheBuster(product.image)} alt={product.name} />
            ) : (
              <div className="image-placeholder">Kein Bild verfügbar</div>
            )}
          </div>

          {/* Produktinformationen */}
          <div className="product-header-info">
            <h1>{product.name}</h1>
            
            {product.description && (
              <p className="product-description">{product.description}</p>
            )}

            <div className="product-meta-info">
              {product.manufacturerId && (
                <div className="meta-item">
                  <span className="meta-label">Hersteller:</span>
                  <span className="meta-value">{typeof product.manufacturerId === 'object' ? product.manufacturerId.name : 'Unknown'}</span>
                </div>
              )}
              
              {/* Quick Info für Dünger */}
              {isFertilizerProduct(product) && (
                <>
                  {product.fertilizerSpecs?.packageSizes?.length > 0 && (
                    <div className="meta-item">
                      <span className="meta-label">Gebindegrößen:</span>
                      <span className="meta-value">{product.fertilizerSpecs.packageSizes.join(', ')}</span>
                    </div>
                  )}
                  {product.fertilizerSpecs?.formulation && (
                    <div className="meta-item">
                      <span className="meta-label">Darreichungsform:</span>
                      <span className="meta-value">{product.fertilizerSpecs.formulation}</span>
                    </div>
                  )}
                  {product.fertilizerSpecs?.phaseSuitability?.length > 0 && (
                    <div className="meta-item">
                      <span className="meta-label">Phasen:</span>
                      <span className="meta-value">{product.fertilizerSpecs.phaseSuitability.map(normalizePhase).join(', ')}</span>
                    </div>
                  )}
                  {product.fertilizerSpecs?.systemCompatibility?.length > 0 && (
                    <div className="meta-item">
                      <span className="meta-label">Anbausystem:</span>
                      <span className="meta-value">{product.fertilizerSpecs.systemCompatibility.join(', ')}</span>
                    </div>
                  )}
                  {product.fertilizerSpecs?.autoIrrigationSuitable !== undefined && (
                    <div className="meta-item">
                      <span className="meta-label">Auto-Bewässerung:</span>
                      <span className="meta-value">{product.fertilizerSpecs.autoIrrigationSuitable ? '✅ Geeignet' : '⚠️ Nicht geeignet'}</span>
                    </div>
                  )}
                  {product.fertilizerSpecs?.npk && (
                    <div className="meta-item">
                      <span className="meta-label">NPK:</span>
                      <span className="meta-value">{product.fertilizerSpecs.npk}</span>
                    </div>
                  )}
                </>
              )}
              
              {product.category && (
                <div className="meta-item">
                  <span className="meta-label">Kategorie:</span>
                  <span className="meta-value">{product.category}</span>
                </div>
              )}
              {product.categories && product.categories.length > 0 && (
                <div className="meta-item">
                  <span className="meta-label">Phasen:</span>
                  <span className="meta-value">{product.categories.filter(c => c.type === 'Phase').map(c => c.name).join(', ') || 'Keine'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Produktspezifikationen */}
      {isFertilizerProduct(product) ? (
        /* Dünger-Spezifikationen - nur Buttons */
        <div className="specs-section specs-section-compact">
          <div className="specs-grid-compact">
            {calcPricePerLiter !== null && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info">
                  💶 €/L Konzentrat<span className="btn-value">{calcPricePerLiter.toFixed(2)}€</span>
                </div>
              </div>
            )}
            {calcSolutionPerEuroVeg !== null && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info tooltip-wrapper"
                     data-tooltip={vegSchedule ? `🌱 Veg-Schema:\n${formatScheduleTooltip(vegSchedule, 'Veg')}` : undefined}>
                  🌱 L/€ Veg<span className="btn-value">{calcSolutionPerEuroVeg.toFixed(1)}L</span>
                </div>
              </div>
            )}
            {calcSolutionPerEuroBloom !== null && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info tooltip-wrapper"
                     data-tooltip={bloomSchedule ? `🌸 Bloom-Schema:\n${formatScheduleTooltip(bloomSchedule, 'Bloom')}` : undefined}>
                  🌸 L/€ Bloom<span className="btn-value">{calcSolutionPerEuroBloom.toFixed(1)}L</span>
                </div>
              </div>
            )}
            {(calcSolutionPerEuroVeg === null && calcSolutionPerEuroBloom === null && calcSolutionPerEuro !== null) && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info">
                  🧪 L Nährlösung/€<span className="btn-value">{calcSolutionPerEuro.toFixed(1)}L</span>
                </div>
              </div>
            )}
            {calcTotalSolutionVeg !== null && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info tooltip-wrapper"
                     data-tooltip={`Aus diesem Gebinde (${bottleSizeLiters}L Konzentrat) können Sie insgesamt ${calcTotalSolutionVeg.toFixed(0)}L fertige Nährlösung anmischen bei ${dosageVeg} ml/L`}>
                  🌱 Lösung aus Gebinde<span className="btn-value">{calcTotalSolutionVeg.toFixed(0)}L</span>
                </div>
              </div>
            )}
            {calcTotalSolutionBloom !== null && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info tooltip-wrapper"
                     data-tooltip={`Aus diesem Gebinde (${bottleSizeLiters}L Konzentrat) können Sie insgesamt ${calcTotalSolutionBloom.toFixed(0)}L fertige Nährlösung anmischen bei ${dosageBloom} ml/L`}>
                  🌸 Lösung aus Gebinde<span className="btn-value">{calcTotalSolutionBloom.toFixed(0)}L</span>
                </div>
              </div>
            )}
            {(calcTotalSolutionVeg === null && calcTotalSolutionBloom === null && calcTotalSolution !== null) && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info tooltip-wrapper"
                     data-tooltip={`Aus diesem Gebinde (${bottleSizeLiters}L Konzentrat) können Sie insgesamt ${calcTotalSolution.toFixed(0)}L fertige Nährlösung anmischen`}>
                  🧃 Lösung aus Gebinde<span className="btn-value">{calcTotalSolution.toFixed(0)}L</span>
                </div>
              </div>
            )}
            {calcCycleCost !== null && (
              <div className="spec-card-compact">
                <div className="btn-inline btn-info tooltip-wrapper"
                     data-tooltip={cycleCostData?.tooltip}>
                  💰 Zyklus/10L<span className="btn-value">{calcCycleCost.toFixed(2)}€</span>
                </div>
              </div>
            )}
            {product.fertilizerSpecs?.nutrientScheduleLink && (
              <div className="spec-card-compact">
                <a href={product.fertilizerSpecs.nutrientScheduleLink} target="_blank" rel="noopener noreferrer" className="btn-inline">
                  📅 Düngeschema
                </a>
              </div>
            )}
            {product.datasheet && (
              <div className="spec-card-compact">
                <a href={product.datasheet} target="_blank" rel="noopener noreferrer" className="btn-inline">
                  📄 Datenblatt
                </a>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Beleuchtungs- und andere Spezifikationen */
        (product.watts || product.umolPerSecond || product.lifespan || product.efficiency) && (
          <div className="specs-section">
            <div className="specs-grid">
              {product.watts && (
                <div className="spec-card">
                  <span className="spec-label">⚡ WATT</span>
                  <span className="spec-value">{product.watts} W</span>
                </div>
              )}
              {product.umolPerSecond && (
                <div className="spec-card">
                  <span className="spec-label">☀️ MMOL/S</span>
                  <span className="spec-value">{product.umolPerSecond}</span>
                </div>
              )}
              {product.efficiency && (
                <div className="spec-card">
                  <span className="spec-label">💡 MMOL/J</span>
                  <span className="spec-value">{product.efficiency} µmol/J</span>
                </div>
              )}
              {product.growTentSize && (
                <div className="spec-card">
                  <span className="spec-label">🌱 ZELTGRÖSSE</span>
                  <span className="spec-value">{product.growTentSize}</span>
                </div>
              )}
              {product.lifespan && (
                <div className="spec-card">
                  <span className="spec-label">💡 LEBENSDAUER</span>
                  <span className="spec-value">{product.lifespan.toLocaleString('de-DE')} h</span>
                </div>
              )}
              {product.watts && bestPrice > 0 && (
                <div className="spec-card">
                  <span className="spec-label">⚡ 💰 WATT/€</span>
                  <span className="spec-value">{(product.watts / bestPrice).toFixed(2)}</span>
                </div>
              )}
              {product.smartControl && product.smartControl !== '' && (
                <div className="spec-card feature-badge-card">
                  <span className="feature-badge smart-control">🎛️ {product.smartControl}</span>
                </div>
              )}
              {product.dimmable && product.dimmable !== 'nicht dimmbar' && (
                <div className="spec-card feature-badge-card">
                  <span className="feature-badge dimmable">🔦 {product.dimmable}</span>
                </div>
              )}
              {isLightingProduct(product) && product.dimmerIncluded && (
                <div className="spec-card feature-badge-card included">
                  <span className="feature-badge dimmer-included">📦 Inklusive Dimmer & Kabel</span>
                </div>
              )}
              {isLightingProduct(product) && !product.dimmerIncluded && (
                <div className="spec-card feature-badge-card warning">
                  <span className="feature-badge missing-dimmer">⚠️ Exklusive Dimmer & Kabel</span>
                </div>
              )}
              {product.datasheet && (
                <div className="spec-card datasheet-card">
                  <a href={product.datasheet} target="_blank" rel="noopener noreferrer" className="btn-datasheet-inline">
                    📄 Datenblatt
                  </a>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* Preisverlauf Chart */}
      <div className="suche-section">
        <PriceHistoryChart productId={id} bestPrice={bestPrice} />
      </div>

      {/* Preisvergleich Tabelle */}
      <div className="suche-section">
        <h2>💰 Preisvergleich ({prices.length} Shops)</h2>

        {prices.length === 0 ? (
          <div className="no-prices-message">
            <p>Noch keine Preise für dieses Produkt vorhanden.</p>
          </div>
        ) : (
          <>
            {/* Zusammenfassung */}
            <div className="price-summary">
              <div className="summary-card">
                <span className="summary-label">Günstigster Preis</span>
                <span className="summary-value">€{bestPrice.toFixed(2)}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Durchschnittspreis</span>
                <span className="summary-value">€{(pricesWithValues.reduce((sum, p) => sum + p.price, 0) / pricesWithValues.length).toFixed(2)}</span>
              </div>
              
              {/* Preisberechnung für Dünger */}
              {isFertilizerProduct(product) && (
                <>
                  {calcPricePerLiter !== null && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">💶 €/L Konzentrat</span>
                      <span className="summary-value">€{calcPricePerLiter.toFixed(2)}</span>
                    </div>
                  )}
                  {calcSolutionPerEuroVeg !== null && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">🌱 L/€ Veg</span>
                      <span className="summary-value">{calcSolutionPerEuroVeg.toFixed(1)} L</span>
                    </div>
                  )}
                  {calcSolutionPerEuroBloom !== null && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">🌸 L/€ Bloom</span>
                      <span className="summary-value">{calcSolutionPerEuroBloom.toFixed(1)} L</span>
                    </div>
                  )}
                  {(calcSolutionPerEuroVeg === null && calcSolutionPerEuroBloom === null && calcSolutionPerEuro !== null) && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">🧪 L Nährlösung/€</span>
                      <span className="summary-value">{calcSolutionPerEuro.toFixed(1)} L</span>
                    </div>
                  )}
                  {calcTotalSolutionVeg !== null && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">🌱 Lösung aus Gebinde</span>
                      <span className="summary-value">{calcTotalSolutionVeg.toFixed(0)} L</span>
                    </div>
                  )}
                  {calcTotalSolutionBloom !== null && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">🌸 Lösung aus Gebinde</span>
                      <span className="summary-value">{calcTotalSolutionBloom.toFixed(0)} L</span>
                    </div>
                  )}
                  {(calcTotalSolutionVeg === null && calcTotalSolutionBloom === null && calcTotalSolution !== null) && (
                    <div className="summary-card price-calc">
                      <span className="summary-label">🧃 Lösung aus Gebinde</span>
                      <span className="summary-value">{calcTotalSolution.toFixed(0)} L</span>
                    </div>
                  )}
                  {calcCycleCost !== null && (
                    <div className="summary-card price-calc tooltip-wrapper"
                         data-tooltip={cycleCostData?.tooltip}>
                      <span className="summary-label">💰 Kosten/Zyklus (10L/Woche)</span>
                      <span className="summary-value">€{calcCycleCost.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Preistabelle */}
            <div className="price-table-container">
              <table className="price-table">
                <thead>
                  <tr>
                    <th className="rank">#</th>
                    <th className="price">Preis</th>
                    <th className="retailer">Händler</th>
                    <th className="delivery">Lieferzeit</th>
                    <th className="stock">Verfügbar</th>
                    <th className="action">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {pricesWithValues.map((price, index) => {
                    const hasPriceValue = price.price !== null && price.price !== undefined && price.price > 0;
                    const positionInPrices = index;
                    const isPriceCard = true;
                    
                    return (
                      <tr 
                        key={price._id || `price-${index}`} 
                        className={`${isPriceCard && positionInPrices === 0 ? 'best-price-row' : ''} ${!hasPriceValue ? 'no-price-row' : ''}`}
                        onClick={() => {
                          if (price.url || price.affiliateUrl) {
                            window.open(price.url || price.affiliateUrl, '_blank');
                          }
                        }}
                        style={(price.url || price.affiliateUrl) ? { cursor: 'pointer' } : {}}
                      >
                        <td className="rank">
                          <span className="rank-badge">
                            {isPriceCard ? (positionInPrices === 0 ? '🥇' : positionInPrices === 1 ? '🥈' : positionInPrices === 2 ? '🥉' : positionInPrices + 1) : '—'}
                          </span>
                        </td>
                        <td className="price">
                          {hasPriceValue ? (
                            <span className={isPriceCard && positionInPrices === 0 ? 'price-best' : 'price-normal'}>
                              €{price.price.toFixed(2)}
                            </span>
                          ) : (
                            <span className="price-not-found">⚠️ Nicht gefunden</span>
                          )}
                        </td>
                        <td className="retailer">
                          <div className="retailer-info">
                            {price.storeLogo && (
                              <img src={price.storeLogo} alt={price.retailer} className="store-logo-small" />
                            )}
                            <span>{price.retailer}</span>
                          </div>
                        </td>
                        <td className="delivery">
                          {price.deliveryTime ? (
                            <span className="delivery-time">🚚 {price.deliveryTime}</span>
                          ) : (
                            <span className="delivery-time unknown">unbekannt</span>
                          )}
                        </td>
                        <td className="stock">
                          {hasPriceValue ? (
                            <span className={`stock-badge ${price.stockStatus === 'in_stock' ? 'in-stock' : price.stockStatus === 'coming_soon' ? 'coming-soon' : 'out-of-stock'}`}>
                              {price.stockStatus === 'in_stock' && '🟢 Verfügbar'}
                              {price.stockStatus === 'coming_soon' && '🟠 Bald wieder'}
                              {price.stockStatus === 'out_of_stock' && '🔴 Ausverkauft'}
                              {!price.stockStatus && (price.inStock ? '✓ Ja' : `✗ ${price.availabilityStatus || 'Ausverkauft'}`)}
                            </span>
                          ) : (
                            <span className="stock-badge unknown">—</span>
                          )}
                        </td>
                        <td className="action">
                          {(price.url || price.affiliateUrl) && (
                            <a 
                              href={price.url || price.affiliateUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn-link"
                            >
                              Zum Shop →
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProductRankingPage;
